import type { PipelineEntry, PipelineStage, SchoolTier } from "@/services/pipeline";
export {
  formatDivision,
  schoolBrandStyle,
  schoolInitials,
  schoolLocation,
} from "@/components/school/schoolBrand";

export const PIPELINE_STAGES: Array<{
  id: PipelineStage;
  label: string;
  shortLabel: string;
}> = [
  { id: "researching", label: "Researching", shortLabel: "Research" },
  { id: "reached_out", label: "Reached out", shortLabel: "Contact" },
  { id: "in_conversation", label: "In conversation", shortLabel: "Talk" },
  { id: "visited", label: "Visited", shortLabel: "Visit" },
  { id: "offered", label: "Offered", shortLabel: "Offer" },
];

export const FIT_TIERS: Array<{ id: SchoolTier; label: string }> = [
  { id: "dream", label: "Dream" },
  { id: "realistic", label: "Realistic" },
  { id: "safety", label: "Safety" },
];

export type PipelineSortOption = "golf" | "fit" | "updated" | "stage" | "name";

function divisionTier(division?: string | null): number {
  const value = String(division || "").toUpperCase();
  if (value === "NCAA DIVISION I" || value === "NCAA D1") return 1;
  if (value === "NCAA DIVISION II" || value === "NCAA D2") return 2;
  if (value === "NCAA DIVISION III" || value === "NCAA D3") return 3;
  if (value === "NAIA") return 4;
  if (value === "NJCAA I" || value === "NJCAA DIVISION I") return 5;
  if (value === "NJCAA II" || value === "NJCAA DIVISION II") return 6;
  if (value === "NJCAA III" || value === "NJCAA DIVISION III") return 7;
  return 8;
}

/**
 * Sort the portfolio without coupling program strength to personal fit. The
 * college-golf ranking is division-specific, so this follows the same division
 * precedence as School Search and then sorts by rank within that division.
 * In-flight fit assessments cannot move cards in the default view.
 */
export function sortPipelineEntries(
  entries: PipelineEntry[],
  sort: PipelineSortOption
): PipelineEntry[] {
  return [...entries].sort((a, b) => {
    if (sort === "golf") {
      const divisionDifference = divisionTier(a.school.division) - divisionTier(b.school.division);
      if (divisionDifference !== 0) return divisionDifference;
      const aRank =
        Number.isFinite(a.school.nationalGolfRank) && (a.school.nationalGolfRank ?? 0) > 0
          ? Number(a.school.nationalGolfRank)
          : Number.POSITIVE_INFINITY;
      const bRank =
        Number.isFinite(b.school.nationalGolfRank) && (b.school.nationalGolfRank ?? 0) > 0
          ? Number(b.school.nationalGolfRank)
          : Number.POSITIVE_INFINITY;
      if (aRank !== bRank) return aRank - bRank;
      return a.school.schoolName.localeCompare(b.school.schoolName);
    }
    if (sort === "fit") return (b.golfFit?.score ?? -1) - (a.golfFit?.score ?? -1);
    if (sort === "updated") {
      return (
        new Date(b.golfFit?.assessedAt || 0).getTime() -
        new Date(a.golfFit?.assessedAt || 0).getTime()
      );
    }
    if (sort === "stage") return stageIndex(b.stage) - stageIndex(a.stage);
    return a.school.schoolName.localeCompare(b.school.schoolName);
  });
}

export function stageIndex(stage: PipelineStage): number {
  return Math.max(
    0,
    PIPELINE_STAGES.findIndex(item => item.id === stage)
  );
}

export function stageLabel(stage: PipelineStage): string {
  return PIPELINE_STAGES.find(item => item.id === stage)?.label || "Researching";
}

export function formatFitDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

export function nextRelationshipAction(entry: PipelineEntry): string {
  if (entry.stage === "researching") return "Review the program and coaching staff";
  if (entry.stage === "reached_out") return "Follow up with the coach";
  if (entry.stage === "in_conversation") return "Share your next tournament update";
  if (entry.stage === "visited") return "Send a visit follow-up";
  return "Review the offer and your next steps";
}

export function fitTierLabel(tier?: SchoolTier | null): string | null {
  if (!tier) return null;
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** Scoring differential for display: one decimal, a true minus sign, and "+" above zero. */
export function formatEvidence(value?: number | null, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return "Not available";
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return (0).toFixed(digits);
  const magnitude = Math.abs(rounded).toFixed(digits);
  return rounded > 0 ? `+${magnitude}` : `\u2212${magnitude}`;
}

/**
 * Plain-language gap between the player and a program. Lower differentials are
 * better, so a player below the program average is ahead of it.
 */
export function differentialGap(player?: number | null, program?: number | null): string | null {
  if (player == null || program == null || !Number.isFinite(player) || !Number.isFinite(program)) {
    return null;
  }
  const gap = Number((player - program).toFixed(1));
  if (Math.abs(gap) < 0.1) return "Right at the program average";
  const strokes = Math.abs(gap).toFixed(1);
  const unit = strokes === "1.0" ? "stroke" : "strokes";
  return gap < 0
    ? `${strokes} ${unit} better than the program average`
    : `${strokes} ${unit} behind the program average`;
}

/** Class rank for display: "#311", or a dash when unknown. Averages round to whole ranks. */
export function formatRank(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `#${Math.round(value)}`;
}

/**
 * Plain-language gap between the player's class rank and the average rank of a
 * program's recent commits. Lower ranks are better. Tiers are based on this.
 */
export function classRankGap(player?: number | null, program?: number | null): string | null {
  if (player == null || program == null || !Number.isFinite(player) || !Number.isFinite(program)) {
    return null;
  }
  const gap = Math.round(player) - Math.round(program);
  if (Math.abs(gap) < 3) return "Right at their recent commits";
  const spots = Math.abs(gap);
  return gap < 0
    ? `${spots} spots ahead of their recent commits`
    : `${spots} spots behind their recent commits`;
}

/** ZON-275 — the My Schools entry to open for ?school=<teamId> (alert email "View school"). */
export function entryIdForSchoolParam(
  entries: { _id: string; school: { teamId: string | null } }[] | undefined,
  teamId: string | null
): string | null {
  if (!teamId || !entries) return null;
  return entries.find(entry => entry.school.teamId === teamId)?._id ?? null;
}

export interface CoachSuggestion {
  message: string;
  action: string;
  prompt: string;
  threadTitle: string;
}

/**
 * One coach suggestion, chosen from the list's balance. Missing tiers come first
 * (Safety, then Dream, then Realistic), then a thin Safety tier; a balanced list
 * gets the general review.
 */
export function coachSuggestion(counts: Record<SchoolTier, number>): CoachSuggestion {
  const findMore = (tier: string): Pick<CoachSuggestion, "prompt" | "threadTitle"> => ({
    prompt: `Help me find more ${tier} programs for My Schools using my latest profile and golf fit. Explain why each one fits, and ask before adding anything to my list.`,
    threadTitle: `Find ${tier} schools`,
  });
  for (const tier of ["safety", "dream", "realistic"] as const) {
    if (counts[tier] === 0) {
      const label = tier.charAt(0).toUpperCase() + tier.slice(1);
      return {
        message: `No ${label} schools yet.`,
        action: `Ask your coach to find ${label} schools`,
        ...findMore(label),
      };
    }
  }
  if (counts.safety === 1) {
    return {
      message: "Only one Safety school so far.",
      action: "Ask your coach to find more",
      ...findMore("Safety"),
    };
  }
  return {
    message: "Want a second look at your list?",
    action: "Ask your coach to review it",
    prompt:
      "Review my current My Schools list using my latest profile and golf data. Tell me what is working, where the list is unbalanced, and which types of programs I should consider next. Keep every school I added, noted, contacted, visited, or otherwise acted on. Propose any additions before making changes.",
    threadTitle: "Review My Schools",
  };
}
